import {
  polygonEdges,
  segmentLength,
  sharedRun,
  WALL_PAIRING,
  type Point,
  type Segment,
} from '@obraplan/core/domain/takeoff/polygon';
import type { Room } from '@obraplan/core/domain/takeoff/room';
import { subSegment } from '@obraplan/core/domain/takeoff/wall-span';
import {
  spanOf,
  wallSideOf,
  withEdgeIndex,
  type Opening,
  type OpeningKind,
  type Wall,
} from '@obraplan/core/domain/takeoff/wall';
import type {
  OpeningId,
  RoomId,
  StructuralElementId,
  WallId,
} from '@obraplan/core/domain/shared/ids';
import type {
  ElementOrientation,
  ElementSegment,
  StructuralElement,
  StructuralElementKind,
} from '@obraplan/core/domain/takeoff/structural-element';

/**
 * A conta do traçado sobre a planta — pura, sem canvas e sem React.
 *
 * O componente desenha e escuta o mouse; **isto** decide onde o ponto cai. Separar as duas
 * coisas é o que permite testar encaixe, ortogonalidade e escala sem montar tela nenhuma —
 * e é onde mora o erro que ninguém percebe olhando: um fator de escala trocado não parece
 * errado no desenho, só no orçamento, três telas depois.
 */

/** Um ponto na tela, em pixels do canvas. */
export interface Pixel {
  readonly x: number;
  readonly y: number;
}

/**
 * A escala da prancha: quantos metros vale um pixel.
 *
 * Sai de uma **cota clicada e digitada**, nunca do "1:50" escrito no desenho. PDF exportado
 * com ajuste de página mente sobre a escala impressa, e esse erro entraria calado em cada
 * metro quadrado da obra.
 */
export function metresPerPixel(a: Pixel, b: Pixel, realLength: number): number | undefined {
  const distancia = Math.hypot(b.x - a.x, b.y - a.y);
  if (distancia < 1 || !Number.isFinite(realLength) || realLength <= 0) return undefined;
  return realLength / distancia;
}

export function pixelToMetres(pixel: Pixel, scale: number): Point {
  return { x: pixel.x * scale, y: pixel.y * scale };
}

export function metresToPixel(point: Point, scale: number): Pixel {
  return { x: point.x / scale, y: point.y / scale };
}

/** Distância entre dois pontos, na unidade em que eles vierem. */
export function distance(a: Pixel | Point, b: Pixel | Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * Encaixa no vértice mais próximo, quando há um perto o bastante.
 *
 * É o que faz a divisa fechar exatamente. Sem encaixe, dois cliques a três pixels de
 * distância viram dois cantos diferentes — e os ambientes deixam de compartilhar parede
 * justamente onde compartilham, porque a sobreposição passa a ser de 3,997 m e não de 4.
 */
export function snapToVertex(
  candidato: Pixel,
  vertices: readonly Pixel[],
  toleranceInPixels: number,
): Pixel {
  let melhor: Pixel | undefined;
  let menor = toleranceInPixels;

  for (const vertice of vertices) {
    const d = distance(candidato, vertice);
    if (d <= menor) {
      menor = d;
      melhor = vertice;
    }
  }
  return melhor ?? candidato;
}

/**
 * Alinha o novo ponto com o anterior quando o traço está quase reto.
 *
 * Parede de planta é ortogonal quase sempre, e a mão treme. Sem isso, um quarto traçado a
 * olho fica com paredes de 3,00 e 3,02 — e o par que faria o retângulo nunca se forma.
 */
export function snapToOrthogonal(
  anterior: Pixel,
  candidato: Pixel,
  toleranceInPixels: number,
): Pixel {
  const dx = Math.abs(candidato.x - anterior.x);
  const dy = Math.abs(candidato.y - anterior.y);

  if (dx <= toleranceInPixels && dx <= dy) return { x: anterior.x, y: candidato.y };
  if (dy <= toleranceInPixels && dy < dx) return { x: candidato.x, y: anterior.y };
  return candidato;
}

export interface SnapOptions {
  readonly vertices?: readonly Pixel[];
  readonly previous?: Pixel;
  readonly vertexTolerance?: number;
  readonly orthogonalTolerance?: number;
}

/**
 * Onde o clique realmente cai.
 *
 * A ordem importa: **vértice primeiro**. Encaixar num canto existente é uma decisão sobre
 * a planta; endireitar é uma correção de pulso. Se a ortogonalidade viesse antes, ela
 * empurraria o ponto para fora do vértice que a pessoa estava mirando.
 */
export function resolvePoint(candidato: Pixel, options: SnapOptions = {}): Pixel {
  const encaixado = snapToVertex(candidato, options.vertices ?? [], options.vertexTolerance ?? 10);
  if (encaixado !== candidato) return encaixado;

  if (options.previous === undefined) return candidato;
  return snapToOrthogonal(options.previous, candidato, options.orthogonalTolerance ?? 8);
}

/** O traçado fecha quando o clique volta ao primeiro vértice. */
export function closesPolygon(
  candidato: Pixel,
  outline: readonly Pixel[],
  toleranceInPixels = 10,
): boolean {
  const primeiro = outline[0];
  return outline.length >= 3 && primeiro !== undefined
    ? distance(candidato, primeiro) <= toleranceInPixels
    : false;
}

/** O retângulo que envolve o contorno — a aproximação que vira largura × comprimento. */
export function boundingBox(outline: readonly Point[]): { width: number; length: number } {
  if (outline.length === 0) return { width: 0, length: 0 };

  const xs = outline.map((ponto) => ponto.x);
  const ys = outline.map((ponto) => ponto.y);
  return {
    width: Math.max(...xs) - Math.min(...xs),
    length: Math.max(...ys) - Math.min(...ys),
  };
}

// ── A estrutura traçada sobre a prancha (Fase 15, fatia 5) ──────────────────────

/**
 * O caminho digitado **não é atalho**, e por isso este arquivo não sabe criar nada: ele só
 * lê o desenho. A planta é opcional no sistema, então cadastrar pilar e viga no cartão da
 * parede continua sendo o caminho principal; o traço é o conforto de quem carregou a
 * prancha e prefere apontar a mirar números.
 *
 * O que o traço responde que a digitação não responde sozinha é **em quais paredes a peça
 * está** — o trabalho que a Fase 14 já tinha tirado das costas de alguém no casamento das
 * divisas, e que voltaria inteiro com a viga que atravessa três paredes.
 */

/** Uma parede com o lugar dela na prancha — reconstruído do contorno dos ambientes. */
export interface PlacedWall {
  readonly wallId: WallId;
  /** De qual ambiente é o lado que a colocou aqui — a divisa aparece sob um dos dois. */
  readonly roomId: RoomId;
  /**
   * Qual lado do contorno ela é.
   *
   * É o que permite mexer no desenho sem refazer a parede: empurrar um lado muda o
   * comprimento **daquela** parede, e a porta, o pilar e o nome dela continuam onde
   * estavam. Sem o índice, a única saída era recriar as paredes do ambiente — e levar
   * junto tudo o que estava pendurado nelas.
   */
  readonly edgeIndex: number;
  readonly segment: Segment;
}

/**
 * Onde cada parede está, em metros.
 *
 * A parede não guarda geometria: ela nasce de um lado do contorno e guarda só o
 * comprimento (ADR-109) — e, desde a Fase 24, **qual** lado ela é (RN-243). Para o traço
 * saber em qual delas o pilar caiu, o lugar precisa ser reconstruído.
 *
 * **Quem lembra o lado vai para ele, sem conta nenhuma.** É o caminho de toda parede
 * nascida do traço. Antes, o lugar saía da medida: a parede de um ambiente nascia na ordem
 * dos lados e com o comprimento deles, e o desenho procurava, lado a lado, a primeira do
 * mesmo tamanho. Com muitas paredes de medida parecida, ou depois de corrigir um número no
 * painel, sobrava parede sem lado — contada no painel, somada no total, e fora do desenho
 * (D22). O índice tira a medida da pergunta.
 *
 * **O legado continua sendo posto como era**, e só nos lados que ninguém reclamou: a parede
 * de antes do índice (RN-244), a acrescentada à mão no painel e a do ambiente redesenhado
 * que perdeu o lado. Para ela vale o casamento de antes — a divisa primeiro e por geometria
 * (o vizinho confirma o lado que encosta nele), depois a primeira do mesmo comprimento.
 *
 * Divisa aparece uma vez só: ela pertence aos dois ambientes, e é desenhada sob o primeiro
 * que a alcança. O lado dela no outro fica ocupado do mesmo jeito — senão o legado do
 * vizinho seria posto por cima dela.
 *
 * Ambiente sem contorno fica de fora: não há geometria a comparar, e chutar um retângulo
 * colocaria paredes em cima da prancha onde ninguém desenhou nada.
 */
export function placeWalls(rooms: readonly Room[], walls: readonly Wall[]): readonly PlacedWall[] {
  const colocadas: PlacedWall[] = [];
  const usadas = new Set<WallId>();
  const contornoDe = new Map(rooms.map((room) => [String(room.id), room.outline]));

  for (const room of rooms) {
    if (room.outline === undefined) continue;
    const lados = polygonEdges(room.outline);
    const doAmbiente = walls.filter(
      (wall) =>
        wall.deletedAt === undefined &&
        (wall.sideA.roomId === room.id || wall.sideB?.roomId === room.id),
    );

    const daqui: PlacedWall[] = [];
    // O lado sem parede — guarda-corpo ou aberto — já está tomado: o legado não é posto
    // ali, porque ali não há parede a pôr (D25).
    const ocupados = new Set<number>((room.edgesWithoutWall ?? []).map((lado) => lado.edgeIndex));
    const semLado: Wall[] = [];
    const inicioDoTrecho = new Map<WallId, number>();

    for (const wall of doAmbiente) {
      const lembrado = wallSideOf(wall, room.id);
      const edgeIndex = lembrado?.edgeIndex;
      const lado = edgeIndex === undefined ? undefined : lados[edgeIndex];
      if (edgeIndex === undefined || lado === undefined) {
        if (!usadas.has(wall.id)) semLado.push(wall);
        continue;
      }
      ocupados.add(edgeIndex);
      if (usadas.has(wall.id)) continue;
      usadas.add(wall.id);
      inicioDoTrecho.set(wall.id, spanOf(lembrado).start);
      // O pedaço do lado que ela ocupa (RN-304): a sala com o quarto encostado no meio tem
      // três paredes naquele lado, e cada uma vai para o trecho dela, sem cobrir a outra.
      daqui.push({
        wallId: wall.id,
        roomId: room.id,
        edgeIndex,
        segment: subSegment(lado, spanOf(lembrado)),
      });
    }

    lados.forEach((lado, edgeIndex) => {
      if (ocupados.has(edgeIndex)) return;
      const medida = segmentLength(lado);
      const parede =
        divisaDoLado(lado, room, semLado, contornoDe, usadas) ??
        semLado.find(
          (candidata) => !usadas.has(candidata.id) && Math.abs(candidata.length - medida) < 0.011,
        );
      if (parede === undefined) return;
      usadas.add(parede.id);
      daqui.push({ wallId: parede.id, roomId: room.id, edgeIndex, segment: lado });
    });

    // Na ordem dos lados, como sempre saiu: quem lê a lista espera a volta do contorno — e,
    // dentro do lado cortado, na ordem do trecho.
    const trechoDe = (posta: PlacedWall): number => inicioDoTrecho.get(posta.wallId) ?? 0;
    colocadas.push(
      ...daqui.sort(
        (uma, outra) => uma.edgeIndex - outra.edgeIndex || trechoDe(uma) - trechoDe(outra),
      ),
    );
  }

  return colocadas;
}

/**
 * As paredes deste ambiente que o desenho não conseguiu pôr.
 *
 * Existem, contam no painel e somam no total — e não aparecem na prancha. Era assim que a
 * parede "sumia" sem ninguém saber por quê (D22); agora a tela diz quantas são e quais
 * (RN-245). A divisa desenhada sob o vizinho **tem** lugar: ela está na prancha, só que
 * pelo outro lado.
 */
export function unplacedWalls(
  room: Room,
  walls: readonly Wall[],
  placed: readonly PlacedWall[],
): readonly Wall[] {
  if (room.outline === undefined) return [];
  const postas = new Set(placed.map((posta) => String(posta.wallId)));
  return walls.filter(
    (wall) =>
      wall.deletedAt === undefined &&
      wallSideOf(wall, room.id) !== undefined &&
      !postas.has(String(wall.id)),
  );
}

/**
 * A parede de antes do índice, com o lado em que o desenho a põe hoje (RN-244).
 *
 * Roda **uma vez**, na chegada do desenho — do servidor ou do rascunho do navegador. O lado
 * que o casamento pela medida acha agora é o que a pessoa está vendo na prancha, e é esse
 * que fica gravado: dali em diante a parede para de depender do comprimento, e corrigir a
 * medida no painel deixa de tirá-la do desenho.
 *
 * A casada ganha os dois lados. O deste ambiente é o do lugar; o do vizinho é o lado dele
 * que anda junto com este — a mesma pergunta que a divisa faz ao ser posta.
 *
 * Quem já lembra não é refeito, e a lista volta a **mesma** quando não há nada a lembrar: o
 * rascunho que não mudou não pode parecer mudado para quem grava a cada mudança.
 */
export function rememberWallEdges(rooms: readonly Room[], walls: readonly Wall[]): readonly Wall[] {
  const contornoDe = new Map(rooms.map((room) => [String(room.id), room.outline]));
  const semLembrar = (side: Wall['sideA'] | undefined): boolean =>
    side !== undefined &&
    side.edgeIndex === undefined &&
    contornoDe.get(String(side.roomId)) !== undefined;
  if (!walls.some((wall) => semLembrar(wall.sideA) || semLembrar(wall.sideB))) return walls;

  const lugar = new Map(placeWalls(rooms, walls).map((posta) => [String(posta.wallId), posta]));
  let mudou = false;

  const lembradas = walls.map((wall) => {
    const posta = lugar.get(String(wall.id));
    if (posta === undefined) return wall;

    let resultado = wall;
    if (wallSideOf(resultado, posta.roomId)?.edgeIndex === undefined) {
      resultado = withEdgeIndex(resultado, posta.roomId, posta.edgeIndex);
    }

    const outro = resultado.sideA.roomId === posta.roomId ? resultado.sideB : resultado.sideA;
    const contorno = outro === undefined ? undefined : contornoDe.get(String(outro.roomId));
    if (outro !== undefined && outro.edgeIndex === undefined && contorno !== undefined) {
      const doVizinho = sideAlong(posta.segment, contorno);
      if (doVizinho !== undefined) resultado = withEdgeIndex(resultado, outro.roomId, doVizinho);
    }

    if (resultado !== wall) mudou = true;
    return resultado;
  });

  return mudou ? lembradas : walls;
}

/**
 * O lado do contorno que anda junto com este trecho — o mais colado, quando dois alcançam.
 *
 * É a pergunta do casamento (`WALL_PAIRING`) feita ao contrário: não "quem encosta neste
 * lado", e sim "qual lado dele encosta aqui". Serve à divisa, que precisa saber o índice
 * dela nos dois ambientes.
 */
export function sideAlong(segment: Segment, outline: readonly Point[]): number | undefined {
  let melhor: { readonly indice: number; readonly gap: number } | undefined;
  polygonEdges(outline).forEach((lado, indice) => {
    const trecho = sharedRun(segment, lado, WALL_PAIRING);
    if (trecho === undefined) return;
    if (melhor === undefined || trecho.gap < melhor.gap) melhor = { indice, gap: trecho.gap };
  });
  return melhor?.indice;
}

/**
 * A parede casada que pertence a **este** lado — a que o vizinho confirma.
 *
 * Uma parede casada tem dois ambientes, e o contorno do outro diz onde ela está: é o lado
 * dele que anda junto com este. Quando dois vizinhos disputam o mesmo lado — o shaft
 * estreito colado no quarto —, ganha o mais próximo, como no casamento.
 */
function divisaDoLado(
  lado: Segment,
  room: Room,
  candidatas: readonly Wall[],
  contornoDe: ReadonlyMap<string, readonly Point[] | undefined>,
  usadas: ReadonlySet<WallId>,
): Wall | undefined {
  let melhor: { readonly wall: Wall; readonly gap: number } | undefined;

  for (const wall of candidatas) {
    if (usadas.has(wall.id) || wall.sideB === undefined) continue;

    const vizinho = wall.sideA.roomId === room.id ? wall.sideB.roomId : wall.sideA.roomId;
    const outroContorno = contornoDe.get(String(vizinho));
    if (outroContorno === undefined) continue;

    for (const ladoDele of polygonEdges(outroContorno)) {
      const trecho = sharedRun(lado, ladoDele, WALL_PAIRING);
      if (trecho === undefined) continue;
      if (melhor === undefined || trecho.gap < melhor.gap) melhor = { wall, gap: trecho.gap };
    }
  }

  return melhor?.wall;
}

// ── Empurrar a parede depois de desenhada ───────────────────────────────────────

/**
 * Um lado de um ambiente — o que o duplo clique agarra.
 *
 * Lado, e não parede: quem se move é o desenho. A parede vem junto porque ela **é** o
 * lado, e é o contorno que manda na medida dela (RN-157).
 */
export interface EdgeRef {
  readonly roomId: RoomId;
  readonly edgeIndex: number;
  readonly segment: Segment;
}

/** Qual lado está debaixo do cursor — o mais perto, dentro da folga. */
export function edgeAt(
  ponto: Point,
  rooms: readonly Room[],
  tolerance: number,
): EdgeRef | undefined {
  let melhor: { readonly edge: EdgeRef; readonly distancia: number } | undefined;

  for (const room of rooms) {
    if (room.outline === undefined) continue;
    polygonEdges(room.outline).forEach((segment, edgeIndex) => {
      const distancia = distanceToSegment(ponto, segment);
      if (distancia > tolerance) return;
      if (melhor === undefined || distancia < melhor.distancia) {
        melhor = { edge: { roomId: room.id, edgeIndex, segment }, distancia };
      }
    });
  }

  return melhor?.edge;
}

/** O contorno de um ambiente depois do empurrão. */
export interface SlidOutline {
  readonly roomId: RoomId;
  readonly outline: readonly Point[];
}

/**
 * Empurra um lado do desenho, **de lado**.
 *
 * A parede de planta anda na perpendicular dela: a vertical vai para os lados, a
 * horizontal sobe e desce. É o ajuste que todo mundo faz depois de traçar por cima de uma
 * prancha — o clique caiu meio metro fora, e redesenhar o ambiente inteiro para corrigir
 * dez centímetros custa a porta, o pilar e o nome de todas as paredes dele.
 *
 * **A divisa leva o vizinho junto.** Empurrar a parede entre a sala e o quarto sem mexer
 * no quarto descolaria os dois: sobraria um vazio de dez centímetros onde havia uma
 * parede, e o casamento que a geometria tinha achado se desfaria no próximo traço. Quem
 * anda junto é quem divide aquele lado (`WALL_PAIRING`), e o afastamento entre os dois se
 * mantém — a espessura do bloco não muda porque alguém corrigiu o desenho.
 */
export function slideEdge(
  rooms: readonly Room[],
  alvo: EdgeRef,
  /** Metros na normal do lado. Positivo é para o lado de fora do traço. */
  distancia: number,
): readonly SlidOutline[] {
  const normal = normalOf(alvo.segment);
  const movidos: SlidOutline[] = [];

  for (const room of rooms) {
    if (room.outline === undefined) continue;
    const lados = polygonEdges(room.outline);
    const alvos = lados
      .map((segment, indice) => ({ segment, indice }))
      .filter(
        ({ segment, indice }) =>
          (room.id === alvo.roomId && indice === alvo.edgeIndex) ||
          sharedRun(alvo.segment, segment, WALL_PAIRING) !== undefined,
      );
    if (alvos.length === 0) continue;

    // Por vértice, e não por lado: o lado empurrado é dois pontos do contorno, e os
    // vizinhos dele se esticam sozinhos por continuarem ligados neles.
    const mover = new Set<number>();
    for (const { indice } of alvos) {
      mover.add(indice);
      mover.add((indice + 1) % room.outline.length);
    }

    movidos.push({
      roomId: room.id,
      outline: room.outline.map((ponto, indice) =>
        mover.has(indice)
          ? {
              x: roundMetres(ponto.x + normal.x * distancia),
              y: roundMetres(ponto.y + normal.y * distancia),
            }
          : ponto,
      ),
    });
  }

  return movidos;
}

/** O quanto o cursor empurrou, medido **na normal** do lado — o resto do gesto se ignora. */
export function slideDistance(alvo: Segment, de: Point, ate: Point): number {
  const normal = normalOf(alvo);
  return roundMetres((ate.x - de.x) * normal.x + (ate.y - de.y) * normal.y);
}

/** A perpendicular unitária do lado. */
function normalOf(segment: Segment): Point {
  const comprimento = segmentLength(segment);
  if (comprimento === 0) return { x: 0, y: 1 };
  return {
    x: -(segment.to.y - segment.from.y) / comprimento,
    y: (segment.to.x - segment.from.x) / comprimento,
  };
}

function distanceToSegment(ponto: Point, segment: Segment): number {
  const dx = segment.to.x - segment.from.x;
  const dy = segment.to.y - segment.from.y;
  const comprimento = Math.hypot(dx, dy);
  if (comprimento === 0) return distance(ponto, segment.from);

  const t = Math.max(
    0,
    Math.min(
      1,
      ((ponto.x - segment.from.x) * dx + (ponto.y - segment.from.y) * dy) / comprimento ** 2,
    ),
  );
  return distance(ponto, { x: segment.from.x + dx * t, y: segment.from.y + dy * t });
}

// ── Fora de esquadro ────────────────────────────────────────────────────────────

/**
 * O lado está no prumo — de pé ou deitado?
 *
 * A folga é de um centímetro, que é a precisão de um clique sobre a prancha: abaixo
 * disso a diferença não existe em obra nenhuma. Acima, existe e não se vê — e é por isso
 * que a tela serrilha o traço torto em vez de esperar alguém conferir número por número.
 */
export function isSquare(segment: Segment, tolerance = 0.01): boolean {
  const dx = Math.abs(segment.to.x - segment.from.x);
  const dy = Math.abs(segment.to.y - segment.from.y);
  return dx <= tolerance || dy <= tolerance;
}

/**
 * O caminho serrilhado — o dente de serra que grita "esta não está reta".
 *
 * Serrilha, e não tracejado: tracejado já quer dizer outra coisa no desenho (o que está
 * fora do plano de corte e o que é posição aproximada), e a diferença entre **torto** e
 * **oculto** precisa ser vista de longe, no meio do traço, sem legenda.
 *
 * Em pixels de tela, porque é aviso de desenho e não medida de obra.
 */
export function serratedPath(de: Pixel, ate: Pixel, amplitude: number, passo: number): string {
  const comprimento = distance(de, ate);
  if (comprimento === 0 || passo <= 0) return `M ${String(de.x)} ${String(de.y)}`;

  const ux = (ate.x - de.x) / comprimento;
  const uy = (ate.y - de.y) / comprimento;
  const nx = -uy;
  const ny = ux;

  // Número **par** de dentes: assim o traço volta ao eixo exatamente na outra ponta, e o
  // canto do ambiente não ganha um degrau que não existe no desenho.
  const dentes = Math.max(2, Math.round(comprimento / passo / 2) * 2);
  const salto = comprimento / dentes;

  let caminho = `M ${String(de.x)} ${String(de.y)}`;
  for (let dente = 1; dente <= dentes; dente += 1) {
    const ao = salto * dente;
    const lado = dente === dentes ? 0 : (dente % 2 === 0 ? -1 : 1) * amplitude;
    caminho += ` L ${String(de.x + ux * ao + nx * lado)} ${String(de.y + uy * ao + ny * lado)}`;
  }
  return caminho;
}

// ── O desenho de volta: cada peça no lugar dela sobre a prancha ─────────────────

/**
 * Nada disto é guardado, e é de propósito.
 *
 * Nem a parede, nem o vão, nem o pilar guardam coordenada: a parede nasce de um lado do
 * contorno (ADR-109) e o resto pendura nela. O desenho reconstrói o lugar de cada um do
 * jeito que `placeWalls` já reconstrói o da parede — e isso é o que faz a planta mostrar
 * **também** o que foi digitado no cartão, por quem nunca abriu a prancha, e continuar
 * mostrando depois que o desenho voltou do servidor noutro navegador.
 *
 * O preço é honesto e está marcado: o que o dado não diz, o desenho não finge saber.
 * `certain: false` é "a peça está nesta parede, e em que ponto dela ninguém registrou" —
 * a tela desenha essas tracejadas.
 */

/** Um vão no lugar dele sobre a parede. A largura é real; a posição, não. */
export interface PlacedOpening {
  readonly openingId: OpeningId;
  readonly kind: OpeningKind;
  /** O trecho da parede que o vão ocupa, em metros. */
  readonly segment: Segment;
  /** Posto pela prancha, e não deduzido: o lugar é sabido, e o traço sai cheio. */
  readonly certain: boolean;
}

/**
 * Onde cada vão está na prancha.
 *
 * O vão posto pela prancha sabe onde está (`offset`), e fica ali: `certain: true`, traço
 * cheio.
 *
 * O cadastrado no cartão guarda largura, altura e quantos são — **não** a que distância do
 * canto fica. Esse é distribuído: o que sobra da parede é repartido em fatias iguais, uma
 * por vão, e cada um fica no meio da sua. Duas janelas de 1,20 numa parede de 4 aparecem
 * espaçadas como aparecem na fachada, e nenhuma delas está exatamente onde o projeto as
 * pôs — é por isso que sai `certain: false`, tracejado, e ninguém mede peitoril por ele.
 */
export function placeOpenings(
  openings: readonly Opening[],
  walls: readonly PlacedWall[],
): readonly PlacedOpening[] {
  const colocados: PlacedOpening[] = [];

  for (const { wallId, segment } of walls) {
    const naParede = openings.filter((opening) => opening.wallId === wallId);
    if (naParede.length === 0) continue;

    const comprimento = segmentLength(segment);
    if (comprimento <= 0) continue;
    const ux = (segment.to.x - segment.from.x) / comprimento;
    const uy = (segment.to.y - segment.from.y) / comprimento;
    const em = (s: number): Point => ({
      x: segment.from.x + ux * s,
      y: segment.from.y + uy * s,
    });

    // O vão posto pela prancha fica onde foi posto, e é lugar sabido: traço cheio.
    const posicionados = naParede.filter(
      (opening) => opening.offset !== undefined && opening.count === 1,
    );
    for (const opening of posicionados) {
      const meio = Math.min(opening.width, comprimento) / 2;
      const inicio = Math.max(0, Math.min(comprimento - meio * 2, opening.offset! - meio));
      colocados.push({
        openingId: opening.id,
        kind: opening.kind,
        segment: { from: em(inicio), to: em(inicio + meio * 2) },
        certain: true,
      });
    }

    // Os demais entram na mesma repartição: duas portas e uma janela são três fatias, e
    // não três centros disputando o meio.
    const deduzidos = naParede.filter((opening) => !posicionados.includes(opening));
    const fatias = deduzidos.reduce((soma, opening) => soma + Math.max(1, opening.count), 0);
    const passo = comprimento / Math.max(1, fatias);
    let indice = 0;

    for (const opening of deduzidos) {
      for (let repeticao = 0; repeticao < Math.max(1, opening.count); repeticao += 1) {
        const centro = passo * (indice + 0.5);
        // O vão não pode sair da parede: numa parede curta a largura é o que couber.
        const meio = Math.min(opening.width, comprimento) / 2;
        const inicio = Math.max(0, Math.min(comprimento - meio * 2, centro - meio));

        colocados.push({
          openingId: opening.id,
          kind: opening.kind,
          segment: { from: em(inicio), to: em(inicio + meio * 2) },
          certain: false,
        });
        indice += 1;
      }
    }
  }

  return colocados;
}

/** Uma peça de estrutura no lugar dela: o retângulo em planta, e se ele é sabido. */
export interface PlacedElement {
  readonly elementId: StructuralElementId;
  readonly kind: StructuralElementKind;
  /** Os quatro cantos, em metros — girados com a parede, como a peça está na prancha. */
  readonly corners: readonly Point[];
  /**
   * O lugar sai do desenho, ou é o melhor palpite?
   *
   * Certo é o pilar de canto — duas paredes concorrentes só se encontram num ponto — e a
   * viga que corre a parede inteira. Palpite é o pilar que só conhece uma parede: ele
   * está nela, e em que ponto dela o cadastro não diz.
   */
  readonly certain: boolean;
  /**
   * O contorno é do próprio elemento (`outline`), e não deduzido das paredes — o pilar sem
   * parede e o pilar e a viga em polígono. Ausente é a peça posta pelos trechos.
   */
  readonly comContorno?: true;
  /** O nome que a peça tem no cadastro, para o rótulo da peça com contorno. */
  readonly name?: string;
}

/** O que ainda conta como duas paredes na mesma direção, e não como um canto. */
const CANTO_MINIMO = 20;

/**
 * Onde cada pilar e cada viga estão na prancha.
 *
 * O trecho (`ElementSegment`) diz **em que paredes** a peça está, e a parede colocada diz
 * onde essa parede está — as duas juntas dizem onde a peça está, nos casos em que isso é
 * dedutível:
 *
 * - **pilar de canto**: duas paredes concorrentes se cruzam num ponto só, e é ali;
 * - **viga deitada**: ela corre pelo plano da parede, e o trecho diz quanto dela;
 * - **viga que atravessa**: o cruzamento com a parte deitada, quando há uma.
 *
 * O elemento com contorno (`outline`) dispensa tudo isso: é desenhado pelo contorno.
 *
 * O resto é palpite marcado: o pilar de uma parede só e a viga que cobre um pedaço ficam
 * no meio do que se sabe, com `certain: false`.
 */
export function placeElements(
  elements: readonly StructuralElement[],
  segments: readonly ElementSegment[],
  walls: readonly PlacedWall[],
): readonly PlacedElement[] {
  const onde = new Map(walls.map((parede) => [String(parede.wallId), parede.segment]));
  const colocados: PlacedElement[] = [];

  for (const element of elements) {
    if (element.deletedAt !== undefined) continue;

    // O contorno manda: o elemento sabe onde está, com parede ou sem ela, e o lugar é
    // sabido (`certain`) — foi traçado, não deduzido.
    if (element.outline !== undefined) {
      colocados.push({
        elementId: element.id,
        kind: element.kind,
        corners: element.outline,
        certain: true,
        comContorno: true,
        ...(element.name === undefined ? {} : { name: element.name }),
      });
      continue;
    }

    const trechos = segments
      .filter((trecho) => trecho.elementId === element.id)
      .map((trecho) => ({ trecho, segment: onde.get(String(trecho.wallId)) }))
      .filter(
        (par): par is { trecho: ElementSegment; segment: Segment } => par.segment !== undefined,
      );
    if (trechos.length === 0) continue;

    if (element.kind === 'PILAR') {
      const pilar = colocarPilar(element, trechos);
      if (pilar !== undefined) colocados.push(pilar);
      continue;
    }

    for (const viga of colocarViga(element, trechos)) colocados.push(viga);
  }

  return colocados;
}

/**
 * O pilar: um retângulo da seção, no canto que as paredes dele determinam.
 *
 * A orientação não é escolha de desenho — é o que o trecho já diz (RN-189): a parede que
 * vê a **largura** é a que corre com ela, e a que vê a profundidade cruza. Desenhar sem
 * olhar isso viraria o 20 × 40 de lado na metade dos pilares de canto.
 */
function colocarPilar(
  element: StructuralElement,
  trechos: readonly { trecho: ElementSegment; segment: Segment }[],
): PlacedElement | undefined {
  // A referência é a parede que **contém** o centro da peça ao longo dela. O traço grampeia a
  // posição na ponta de quem termina antes do centro — o pedaço livre de 1,5 m ao lado da
  // divisa, num pilar desenhado sobre o canto do quarto —, e medir por ele empurrava o
  // pilar até a ponta do pedaço no instante do cadastro.
  const contem = trechos.find(contemOCentro);
  const principal = contem ?? trechos[0]!;
  const cruzada = trechos.find(
    (outro) => angleBetween(principal.segment, outro.segment) >= CANTO_MINIMO,
  );
  // Nenhuma parede contém o centro: o pilar passou da ponta de todas — o de fora do canto.
  // A posição ao longo grampeou, mas o lado não grampeia: o centro é o cruzamento das duas
  // paredes, cada uma deslocada pelo lado que o traço mediu.
  const peloCanto = contem === undefined ? centroPelosLados(principal, cruzada) : undefined;

  /*
   * A posição desenhada manda — inclusive sobre o encontro das paredes.
   *
   * Quem traçou viu onde a peça está, e o cruzamento é dedução. Nos pilares de canto os
   * dois coincidem; nos demais, só a posição sabe. Sem ela, volta o palpite de antes:
   * cruzamento quando há duas paredes concorrentes, meio da parede quando há uma só.
   */
  const desenhado = principal.trecho.offset;
  const centro =
    peloCanto ??
    (desenhado !== undefined
      ? aoLado(
          pointAlong(principal.segment, desenhado),
          principal.segment,
          principal.trecho.lateral,
        )
      : cruzada === undefined
        ? midpoint(principal.segment)
        : (lineIntersection(principal.segment, cruzada.segment) ?? midpoint(principal.segment)));

  const direcao = unit(principal.segment);
  // `PARALELA` é "esta parede vê a largura": a largura corre com ela.
  const aoLongo =
    principal.trecho.orientation === 'PARALELA' ? element.sectionWidth : element.sectionDepth;
  const atravessado =
    principal.trecho.orientation === 'PARALELA' ? element.sectionDepth : element.sectionWidth;

  return {
    elementId: element.id,
    kind: 'PILAR',
    corners: rectangleAround(centro, direcao, aoLongo, atravessado),
    certain: desenhado !== undefined || cruzada !== undefined,
  };
}

/**
 * A viga: uma faixa por parede em que ela deita, e uma marca onde ela atravessa.
 *
 * A faixa é o desenho de verdade — a viga corre pelo plano da parede, com a largura dela.
 * A marca da perpendicular é curta de propósito: ali a viga só **passa**, e desenhá-la
 * cruzando o ambiente inteiro inventaria um comprimento que o cadastro não tem.
 */
function colocarViga(
  element: StructuralElement,
  trechos: readonly { trecho: ElementSegment; segment: Segment }[],
): readonly PlacedElement[] {
  const deitada = trechos.filter(({ trecho }) => trecho.orientation === 'PARALELA');
  const atravessa = trechos.filter(({ trecho }) => trecho.orientation === 'PERPENDICULAR');
  const desenhos: PlacedElement[] = [];

  for (const { trecho, segment } of deitada) {
    const daParede = segmentLength(segment);
    const corrida = Math.min(trecho.length ?? daParede, daParede);
    const centro =
      trecho.offset === undefined
        ? midpoint(segment)
        : aoLado(pointAlong(segment, trecho.offset), segment, trecho.lateral);
    desenhos.push({
      elementId: element.id,
      kind: 'VIGA',
      corners: rectangleAround(centro, unit(segment), corrida, element.sectionWidth),
      // Cobrindo a parede inteira, o lugar é o da parede. Cobrindo um pedaço, quem sabe
      // qual pedaço é o desenho: sem a posição, o palpite centraliza e avisa.
      certain: trecho.length === undefined || trecho.offset !== undefined,
    });
  }

  for (const { trecho, segment } of atravessa) {
    const cruzamento =
      trecho.offset !== undefined
        ? pointAlong(segment, trecho.offset)
        : deitada
            .map(({ segment: outra }) => lineIntersection(segment, outra))
            .find((ponto): ponto is Point => ponto !== undefined);

    desenhos.push({
      elementId: element.id,
      kind: 'VIGA',
      corners: rectangleAround(
        cruzamento ?? midpoint(segment),
        unit(segment),
        element.sectionWidth,
        // Uma marca, e não uma viga inteira: o suficiente para atravessar a parede.
        Math.max(element.sectionWidth, 0.3),
      ),
      certain: cruzamento !== undefined,
    });
  }

  return desenhos;
}

/**
 * A posição guardada caiu **dentro** da parede, e não grampeada numa ponta.
 *
 * O traço mede a posição de cada parede que a peça toca, e grampeia em 0 ou no comprimento
 * quando o centro da peça passa da ponta dela. Grampeada, a posição diz onde a parede
 * acaba, e não onde a peça está.
 */
function contemOCentro({
  trecho,
  segment,
}: {
  readonly trecho: ElementSegment;
  readonly segment: Segment;
}): boolean {
  if (trecho.offset === undefined) return false;
  return trecho.offset > 0.005 && trecho.offset < segmentLength(segment) - 0.005;
}

/**
 * O centro do pilar de canto pelos dois lados medidos: a reta de cada parede, deslocada pelo
 * `lateral` dela, e o cruzamento das duas. Sem as duas paredes ou sem o lado de alguma,
 * não há o que cruzar.
 */
function centroPelosLados(
  principal: { readonly trecho: ElementSegment; readonly segment: Segment },
  cruzada: { readonly trecho: ElementSegment; readonly segment: Segment } | undefined,
): Point | undefined {
  if (cruzada === undefined) return undefined;
  const a = principal.trecho.lateral;
  const b = cruzada.trecho.lateral;
  if (a === undefined || b === undefined) return undefined;
  const deslocada = (segment: Segment, metros: number): Segment => ({
    from: aoLado(segment.from, segment, metros),
    to: aoLado(segment.to, segment, metros),
  });
  return lineIntersection(deslocada(principal.segment, a), deslocada(cruzada.segment, b));
}

/** O ângulo entre dois segmentos, em graus, sem olhar o sentido — 0 é paralelo. */
function angleBetween(a: Segment, b: Segment): number {
  const ua = unit(a);
  const ub = unit(b);
  const cosseno = Math.min(1, Math.abs(ua.x * ub.x + ua.y * ub.y));
  return (Math.acos(cosseno) * 180) / Math.PI;
}

function unit(segment: Segment): Point {
  const comprimento = segmentLength(segment);
  if (comprimento === 0) return { x: 1, y: 0 };
  return {
    x: (segment.to.x - segment.from.x) / comprimento,
    y: (segment.to.y - segment.from.y) / comprimento,
  };
}

/**
 * O ponto a `metros` do início da parede, andando por ela.
 *
 * Grampeado nas duas pontas: posição maior que a parede é desenho que escorregou na
 * conversão, e deixá-lo passar poria a peça flutuando fora do contorno — longe de onde
 * alguém clicou, que é exatamente o defeito que a posição veio consertar.
 */
function pointAlong(segment: Segment, metres: number): Point {
  const comprimento = segmentLength(segment);
  if (comprimento === 0) return segment.from;
  const andado = Math.min(Math.max(metres, 0), comprimento);
  const direcao = unit(segment);
  return { x: segment.from.x + direcao.x * andado, y: segment.from.y + direcao.y * andado };
}

/**
 * A parede sob o clique, e onde nela ele caiu — o alvo de quem insere um vão.
 *
 * A mais próxima dentro da folga, e não a primeira: no canto duas paredes disputam o
 * mesmo clique, e a que está mais perto é a que a pessoa mirou.
 */
export function wallAt(
  ponto: Point,
  walls: readonly PlacedWall[],
  tolerance: number,
): { readonly wallId: WallId; readonly offset: number } | undefined {
  let melhor: { readonly wallId: WallId; readonly offset: number; readonly d: number } | undefined;
  for (const { wallId, segment } of walls) {
    const d = distanceToSegment(ponto, segment);
    if (d > tolerance || (melhor !== undefined && d >= melhor.d)) continue;
    melhor = { wallId, offset: roundMetres(offsetAlong(segment, ponto)), d };
  }
  return melhor === undefined ? undefined : { wallId: melhor.wallId, offset: melhor.offset };
}

/**
 * O ponto deslocado para o lado da parede — a esquerda de quem anda do início ao fim.
 *
 * É a mesma normal do `rectangleAround`, para que "lado" queira dizer a mesma coisa no
 * traço que mede e no desenho que recoloca.
 */
function aoLado(ponto: Point, segment: Segment, metros: number | undefined): Point {
  if (metros === undefined || metros === 0) return ponto;
  const direcao = unit(segment);
  return { x: ponto.x - direcao.y * metros, y: ponto.y + direcao.x * metros };
}

/** Quanto o ponto está fora do eixo da parede, para a esquerda dela — o inverso de `aoLado`. */
function lateralFrom(segment: Segment, point: Point): number {
  const direcao = unit(segment);
  return (point.x - segment.from.x) * -direcao.y + (point.y - segment.from.y) * direcao.x;
}

/** Quanto da parede corre antes do ponto — a projeção dele sobre ela, grampeada. */
function offsetAlong(segment: Segment, point: Point): number {
  const comprimento = segmentLength(segment);
  if (comprimento === 0) return 0;
  const direcao = unit(segment);
  const projecao = (point.x - segment.from.x) * direcao.x + (point.y - segment.from.y) * direcao.y;
  return Math.min(Math.max(projecao, 0), comprimento);
}

function midpoint(segment: Segment): Point {
  return {
    x: (segment.from.x + segment.to.x) / 2,
    y: (segment.from.y + segment.to.y) / 2,
  };
}

/**
 * Onde as **retas** de dois segmentos se cruzam.
 *
 * Reta, e não segmento: o canto do ambiente é o encontro de dois lados que podem não
 * chegar a se tocar quando cada um foi traçado pela face da sua parede — é justamente o
 * caso que a folga do casamento resolve, e o cruzamento precisa enxergá-lo igual.
 */
function lineIntersection(a: Segment, b: Segment): Point | undefined {
  const ax = a.to.x - a.from.x;
  const ay = a.to.y - a.from.y;
  const bx = b.to.x - b.from.x;
  const by = b.to.y - b.from.y;

  const denominador = ax * by - ay * bx;
  if (Math.abs(denominador) < 1e-9) return undefined;

  const t = ((b.from.x - a.from.x) * by - (b.from.y - a.from.y) * bx) / denominador;
  return { x: a.from.x + ax * t, y: a.from.y + ay * t };
}

/** O retângulo da peça: tanto ao longo da direção dada, tanto atravessado nela. */
function rectangleAround(
  centro: Point,
  direcao: Point,
  aoLongo: number,
  atravessado: number,
): readonly Point[] {
  const meioAoLongo = aoLongo / 2;
  const meioAtravessado = atravessado / 2;
  const normalX = -direcao.y;
  const normalY = direcao.x;

  return [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ].map(([aoLongoSinal, atravessadoSinal]) => ({
    x:
      centro.x +
      direcao.x * meioAoLongo * aoLongoSinal! +
      normalX * meioAtravessado * atravessadoSinal!,
    y:
      centro.y +
      direcao.y * meioAoLongo * aoLongoSinal! +
      normalY * meioAtravessado * atravessadoSinal!,
  }));
}

/** O trecho que o traço determinou: a parede, como a peça a encontra, e quanto dela ocupa. */
export interface TracedSegment {
  readonly wallId: WallId;
  readonly orientation: ElementOrientation;
  /** Ausente é a parede inteira — o padrão da viga que nasce em cima dela. */
  readonly length?: number;
  /** Onde, ao longo desta parede, ficou o centro do que se desenhou — em metros. */
  readonly offset?: number;
  /** Quanto o centro ficou fora do eixo da parede, em metros, com sinal. */
  readonly lateral?: number;
}

export interface TracedStructure {
  /** Metros. Pilar: a largura em planta. Viga: a largura dela. */
  readonly sectionWidth: number;
  /** Metros. Pilar: a profundidade em planta. Viga: **não sai da planta** — é a altura. */
  readonly sectionDepth?: number;
  /** A corrida que a planta mostra: o comprimento da viga. O pilar sobe, e a planta não vê. */
  readonly run?: number;
  readonly segments: readonly TracedSegment[];
}

/**
 * A folga do traço sobre a prancha, em metros.
 *
 * O contorno foi clicado sobre a linha da parede e o retângulo do pilar é desenhado dentro
 * dela; cinco centímetros perdoam o clique sem alcançar a parede de trás, que está a pelo
 * menos uma espessura de distância.
 */
const FOLGA_DO_TRACO = 0.05;

/**
 * O que dois cliques em cantos opostos dizem sobre a peça.
 *
 * **Pilar**: o retângulo é a seção em planta, e ela sai inteira — largura e profundidade. A
 * altura não sai: pilar sobe, e a planta é vista de cima. É digitada, como o pé-direito.
 *
 * **Viga**: o lado maior é a corrida e o menor é a largura dela. A **altura da seção**
 * também não sai da planta — e é justamente ela que come a alvenaria (RN-183), por isso a
 * tela a pede antes de aceitar o traço, em vez de nascer com um palpite que ninguém veria.
 *
 * A orientação de cada trecho sai da direção da parede, e não de uma escolha: a parede que
 * corre no eixo do lado maior da viga é a que ela deita em cima (PARALELA); a que cruza
 * esse eixo é a que ela atravessa (PERPENDICULAR). No pilar a pergunta é outra — qual face
 * aquela parede vê (RN-189) —, e a resposta é o lado do retângulo que corre com ela.
 */
export function traceStructure(
  kind: StructuralElementKind,
  a: Point,
  b: Point,
  walls: readonly PlacedWall[],
): TracedStructure {
  const min = { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y) };
  const max = { x: Math.max(a.x, b.x), y: Math.max(a.y, b.y) };
  const emX = max.x - min.x;
  const emY = max.y - min.y;

  const folgado = {
    min: { x: min.x - FOLGA_DO_TRACO, y: min.y - FOLGA_DO_TRACO },
    max: { x: max.x + FOLGA_DO_TRACO, y: max.y + FOLGA_DO_TRACO },
  };

  const maiorEmX = emX >= emY;
  const centroDoTraco = { x: (min.x + max.x) / 2, y: (min.y + max.y) / 2 };
  const achados: { readonly trecho: TracedSegment; readonly naPonta: boolean }[] = [];

  for (const { wallId, segment } of walls) {
    if (!crossesRect(segment, folgado.min, folgado.max)) continue;

    const paredeEmX =
      Math.abs(segment.to.x - segment.from.x) >= Math.abs(segment.to.y - segment.from.y);
    const orientation: ElementOrientation =
      kind === 'PILAR'
        ? // A parede que corre em x vê o lado em x do retângulo, que é a largura.
          paredeEmX
          ? 'PARALELA'
          : 'PERPENDICULAR'
        : paredeEmX === maiorEmX
          ? 'PARALELA'
          : 'PERPENDICULAR';

    // Só a viga deitada mede trecho: a perpendicular deixa a seção, e o pilar corre para
    // cima. Coberta inteira, o campo fica **ausente** — que é como o domínio diz "a parede
    // toda", e é o que continua certo quando alguém corrigir o comprimento dela.
    const comprimento =
      kind === 'VIGA' && orientation === 'PARALELA'
        ? overlapAlong(segment, folgado.min, folgado.max)
        : undefined;
    const daParede = segmentLength(segment);

    achados.push({
      trecho: {
        wallId,
        orientation,
        ...(comprimento === undefined || comprimento >= daParede - 0.02
          ? {}
          : { length: roundMetres(comprimento) }),
        // Onde o traço caiu nesta parede. É o que faltava para o desenho parar de
        // recolocar a peça no meio dela.
        offset: roundMetres(offsetAlong(segment, centroDoTraco)),
        // E para o lado: sem isto a peça voltava centrada no eixo, e o pilar encostado por
        // dentro do ambiente saltava meia espessura ao ser cadastrado.
        lateral: roundMetres(lateralFrom(segment, centroDoTraco)),
      },
      naPonta: orientation === 'PERPENDICULAR' && naPontaDoEixo(segment, min, max, maiorEmX),
    });
  }

  const segments = manterTrechos(kind, achados);

  if (kind === 'PILAR') {
    return { sectionWidth: roundMetres(emX), sectionDepth: roundMetres(emY), segments };
  }
  return {
    sectionWidth: roundMetres(Math.min(emX, emY)),
    run: roundMetres(Math.max(emX, emY)),
    segments,
  };
}

/** Duas casas, como toda medida da geometria — o traço não tem precisão de milímetro. */
function roundMetres(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * A parede cruza a peça no meio dela, ou encosta na ponta?
 *
 * A pergunta separa **atravessar** de **terminar**, e as duas não são a mesma coisa em
 * planta: a parede que corta o eixo da viga no meio dela é atravessada; a que está na ponta
 * é onde a viga acaba.
 */
function naPontaDoEixo(segment: Segment, min: Point, max: Point, maiorEmX: boolean): boolean {
  const posicao = maiorEmX
    ? (segment.from.x + segment.to.x) / 2
    : (segment.from.y + segment.to.y) / 2;
  const inicio = maiorEmX ? min.x : min.y;
  const fim = maiorEmX ? max.x : max.y;

  return posicao <= inicio + FOLGA_DO_TRACO || posicao >= fim - FOLGA_DO_TRACO;
}

/**
 * Quais trechos sobrevivem ao traço.
 *
 * **A viga que deita numa parede termina nas paredes do canto — ela não as atravessa.** Uma
 * viga traçada de ponta a ponta da parede de cima encosta na da esquerda e na da direita, e
 * registrá-las descontaria, de cada uma, um retângulo de bloco onde a peça só dobra a
 * esquina. Quem quiser mesmo o trecho continua tendo o "+ cruza também…" no cartão.
 *
 * A viga que **não** deita em parede nenhuma é outra história: ela cruza a sala e as paredes
 * das pontas são onde ela se apoia — ali o retângulo da seção está lá mesmo, e o trecho
 * entra (RN-183).
 *
 * No pilar a poda não se aplica: o pilar de canto pertence às duas paredes de propósito, e
 * cada uma vê uma face diferente dele (RN-189).
 */
function manterTrechos(
  kind: StructuralElementKind,
  achados: readonly { readonly trecho: TracedSegment; readonly naPonta: boolean }[],
): readonly TracedSegment[] {
  const deitada = achados.some(({ trecho }) => trecho.orientation === 'PARALELA');
  if (kind === 'PILAR' || !deitada) return achados.map(({ trecho }) => trecho);

  return achados.filter(({ naPonta }) => !naPonta).map(({ trecho }) => trecho);
}

/**
 * O segmento atravessa o retângulo?
 *
 * Recorte de Liang–Barsky: o segmento vira um parâmetro de 0 a 1 e cada borda do retângulo
 * corta um pedaço dele. Sobrando intervalo, houve travessia. É mais do que testar as
 * pontas — a parede pode entrar por um lado e sair pelo outro sem ter ponta nenhuma
 * dentro, que é exatamente o caso da viga cruzando a parede.
 */
function crossesRect(segment: Segment, min: Point, max: Point): boolean {
  const dx = segment.to.x - segment.from.x;
  const dy = segment.to.y - segment.from.y;
  let inicio = 0;
  let fim = 1;

  const cortar = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0;
    const r = q / p;
    if (p < 0) {
      if (r > fim) return false;
      if (r > inicio) inicio = r;
      return true;
    }
    if (r < inicio) return false;
    if (r < fim) fim = r;
    return true;
  };

  return (
    cortar(-dx, segment.from.x - min.x) &&
    cortar(dx, max.x - segment.from.x) &&
    cortar(-dy, segment.from.y - min.y) &&
    cortar(dy, max.y - segment.from.y)
  );
}

/**
 * Quanto da parede o retângulo cobre, medido **no eixo dela**.
 *
 * É o trecho da viga: ela pode correr dois metros e meio de uma parede de quatro, e cobrar
 * a parede inteira seria descontar alvenaria que está lá.
 */
function overlapAlong(segment: Segment, min: Point, max: Point): number {
  const dx = segment.to.x - segment.from.x;
  const dy = segment.to.y - segment.from.y;
  const comprimento = Math.hypot(dx, dy);
  if (comprimento === 0) return 0;

  const ux = dx / comprimento;
  const uy = dy / comprimento;
  const projeta = (ponto: Point): number =>
    (ponto.x - segment.from.x) * ux + (ponto.y - segment.from.y) * uy;

  // Os quatro cantos: o retângulo é alinhado aos eixos, a parede não precisa ser.
  const projecoes = [
    projeta(min),
    projeta({ x: max.x, y: min.y }),
    projeta({ x: min.x, y: max.y }),
    projeta(max),
  ];

  const inicio = Math.max(0, Math.min(...projecoes));
  const fim = Math.min(comprimento, Math.max(...projecoes));
  return Math.max(0, fim - inicio);
}
